import { db } from '../db/index.js';
import { supabase } from '../lib/supabase.js';
import { createSyncEngine } from './engine.js';
import { createSupabaseTransport } from './transport.js';
import { ensureOwner } from './account.js';
import { setSyncState, useSyncStore } from './store.js';
import { getMeta } from '../db/meta.js';
import { maybeDailyCloudSnapshot } from '../backup/cloudSnapshots.js';
import { queryClient } from '../api/queryClient.js';
import { queryKeys } from '../api/keys.js';
import { useSettings } from '../app/settingsStore.js';
import { activateSettingsUser, installSettingsBridge, settleActiveSettings } from './settingsSync.js';
import { toast } from '../components/Toaster.jsx';

installSettingsBridge();

let engine = null;
let unsubscribeRealtime = null;
let currentUserId = null;
let realtimeTimer = null;
let switching = Promise.resolve();

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('recall-sync') : null;
channel?.addEventListener('message', (e) => {
  if (e.data?.type === 'synced' && e.data.at > (useSyncStore.getState().lastSyncAt ?? 0)) {
    setSyncState({ lastSyncAt: e.data.at });
  }
});

async function afterSync(res) {
  channel?.postMessage({ type: 'synced', at: Date.now() });
  if (currentUserId && res && !res.aborted && !res.error) {
    try {
      await settleActiveSettings(currentUserId);
    } catch (e) {
      console.warn('Settings sync skipped:', e.message);
    }
  }
  if (!currentUserId || !useSettings.getState().autoCloudBackup) return;
  try {
    const path = await maybeDailyCloudSnapshot(db, currentUserId);
    if (path) {
      setSyncState({ lastCloudBackupAt: Date.now() });
      queryClient.invalidateQueries({ queryKey: queryKeys.backups(currentUserId) });
    }
  } catch (e) {
    console.warn('Cloud backup skipped:', e.message);
  }
}

async function stopEngine() {
  clearTimeout(realtimeTimer);
  realtimeTimer = null;
  const pending = engine?.stop() ?? Promise.resolve();
  engine = null;
  unsubscribeRealtime?.();
  unsubscribeRealtime = null;
  await pending;
}

/** Called whenever the signed-in user changes (including to null). */
export function onUserChanged(user) {
  switching = switching.then(() => applyUser(user)).catch((e) => {
    console.error(e);
    setSyncState({ status: 'error', lastError: e.message });
  });
  return switching;
}

async function applyUser(user) {
  const id = user?.id ?? null;
  if (id === currentUserId && (engine || !id)) return;
  await stopEngine();
  currentUserId = id;
  await activateSettingsUser(id);
  if (!id || !supabase) {
    setSyncState({ status: 'local' });
    return;
  }
  const outcome = await ensureOwner(db, id);
  if (outcome === 'linked') toast.success('Signed in. Uploading your cards to your account…');
  if (outcome === 'switched') {
    toast.info('Switched accounts. The previous account’s data was saved as a local snapshot.', { duration: 8000 });
  }
  setSyncState({ lastCloudBackupAt: await getMeta(db, 'lastCloudBackupAt') });
  const transport = createSupabaseTransport(supabase);
  engine = createSyncEngine({ db, transport, onState: setSyncState, onSynced: afterSync });
  engine.start();
  unsubscribeRealtime = transport.subscribe(id, () => {
    clearTimeout(realtimeTimer);
    realtimeTimer = setTimeout(() => engine?.request('realtime'), 800);
  });
}

export function syncNow(opts) {
  return engine ? engine.syncNow(opts) : Promise.resolve(null);
}

export function isSyncActive() {
  return !!engine;
}
